import React from 'react';
import { safeStringify } from '../utils/safeStringify';

/**
 * ISSUE #1 FIX: ErrorBoundary to catch and display React errors safely
 * 
 * Prevents the entire app from crashing when a component throws an error.
 * Displays a user-friendly error message instead.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      // Fallback UI when error occurs
      return (
        <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-2xl font-serif text-black mb-2">Something went wrong</h2>
              <p className="text-sm text-stone-500 mb-6">
                {safeStringify(this.state.error, 'An unexpected error occurred. Please try refreshing the page.')}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="bg-black hover:bg-[#FF7A45] text-white px-6 py-3 rounded-lg font-sans font-medium uppercase tracking-wider text-sm transition-all duration-300"
              >
                Refresh Page
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
              <details className="mt-6 text-xs text-stone-500">
                <summary className="cursor-pointer font-semibold mb-2">Error Details (Dev Only)</summary>
                <pre className="whitespace-pre-wrap overflow-auto p-3 bg-gray-50 rounded">
                  {safeStringify(this.state.errorInfo.componentStack)}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
